import { Mark, mergeAttributes } from "@tiptap/core";

export interface FontSizeOptions {
  HTMLAttributes: Record<string, unknown>;
}

export const FONT_SIZE_NAME = "fontSize";

/** 툴바 글자 크기 프리셋(라벨/값). value=null 은 기본(마크 해제). 값은 CSS font-size. */
export const FONT_SIZE_PRESETS: ReadonlyArray<{ label: string; value: string | null }> = [
  { label: "기본", value: null },
  { label: "작게", value: "12px" },
  { label: "보통", value: "14px" },
  { label: "크게", value: "18px" },
  { label: "더 크게", value: "24px" },
  { label: "제목", value: "32px" },
];

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      /** 선택 영역에 글자 크기 적용(예: "18px"). */
      setFontSize: (size: string) => ReturnType;
      /** 글자 크기 마크 해제(기본 크기로 복귀). */
      unsetFontSize: () => ReturnType;
    };
  }
}

/**
 * 인라인 글자 크기 마크 — `<span style="font-size:Npx">` 렌더/파싱.
 * 의존성 없는 자체 구현(@tiptap/core)으로 TextStyle 패키지 불필요.
 * 토큰/이미지 라운드트립과 독립: detokenizeHtml 정규식은 data-blip-token span 만 처리하므로
 * 글자 크기 span 은 저장 HTML 에 그대로 보존된다.
 */
export const FontSize = Mark.create<FontSizeOptions>({
  name: FONT_SIZE_NAME,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).style.fontSize || null,
        renderHTML: (attrs) => (attrs.size ? { style: `font-size: ${attrs.size}` } : {}),
      },
    };
  },

  parseHTML() {
    // font-size inline 스타일이 있는 span 만 매칭(data-blip-token span 등은 제외).
    return [
      {
        tag: "span",
        getAttrs: (el) => ((el as HTMLElement).style.fontSize ? {} : false),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setFontSize:
        (size: string) =>
        ({ chain }) =>
          chain().setMark(FONT_SIZE_NAME, { size }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().unsetMark(FONT_SIZE_NAME).run(),
    };
  },
});
